import {
  getTrackColor,
  getTrackColorKeyFromFeature,
  getHourColor,
  extractTimedCoordinates
} from '../../../../utils/trackColorUtil';
import { isCodeLikeName } from '../../../../utils/agentUtil';
import { toColouredSegments } from './tracksFullColour';

/**
 * Above this many simultaneously visible tracks, 'Track centerpoints' (the
 * default track display mode) is force-switched to 'Track heatmap' (see
 * MapComponent.enforceCenterpointsTrackLimit) instead of left to degrade
 * silently.
 *
 * Same "no published magic number" caveat as MAX_COLOUR_CODED_TRACKS in
 * tracksFullColour.js applies here too, plus a rendering-cost angle on top:
 * updateActualTracksLayers() rebuilds every point along every visible
 * track's geometry from scratch (see tracePointFeatures below) and
 * re-uploads the full GeoJSON via source.setData() on every relevant prop
 * change (pan, agent-colour toggle, track selection) - there is no
 * per-track cache here the way tracksFullColour/tracksFullStandard have
 * (see trackSegmentCache.js). More tracks means more points means a
 * costlier rebuild on every one of those changes, on top of the same
 * overplotting/legibility ceiling. 1000 is a deliberately conservative
 * order-of-magnitude safeguard, not a measured breakpoint for this specific
 * app - adjust if profiling on real data/hardware shows more headroom.
 */
export const MAX_CENTERPOINT_TRACKS = 1000;

export function updateActualTracksLayers() {
  const selectedAgentValues = this.getSelectedAgentMatchValues();
  const coordsAreNear = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2)
      return false;
    const ax = Number(a[0]);
    const ay = Number(a[1]);
    const bx = Number(b[0]);
    const by = Number(b[1]);
    if (
      !Number.isFinite(ax) ||
      !Number.isFinite(ay) ||
      !Number.isFinite(bx) ||
      !Number.isFinite(by)
    ) {
      return false;
    }
    return Math.abs(ax - bx) < 1e-5 && Math.abs(ay - by) < 1e-5;
  };
  const getCoordinateDistanceSq = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2)
      return Number.POSITIVE_INFINITY;
    const ax = Number(a[0]);
    const ay = Number(a[1]);
    const bx = Number(b[0]);
    const by = Number(b[1]);
    if (
      !Number.isFinite(ax) ||
      !Number.isFinite(ay) ||
      !Number.isFinite(bx) ||
      !Number.isFinite(by)
    ) {
      return Number.POSITIVE_INFINITY;
    }
    const dx = ax - bx;
    const dy = ay - by;
    return dx * dx + dy * dy;
  };
  const selectedItemProperties =
    (this.props.selectedItem && this.props.selectedItem.properties) || {};
  const selectedItemEntityType = String(
    selectedItemProperties.EntityType ||
      this.props.selectedItem?.featureType ||
      ''
  ).toLowerCase();
  const isTrackSelectedItem =
    selectedItemEntityType.indexOf('track/') === 0 ||
    selectedItemEntityType === 'track';
  const selectedTrackId =
    isTrackSelectedItem &&
    this.props.selectedItem &&
    (this.props.selectedItem.featureId ||
      selectedItemProperties.EntityId ||
      selectedItemProperties.trackId ||
      selectedItemProperties.TrackId)
      ? String(
          this.props.selectedItem.featureId ||
            selectedItemProperties.EntityId ||
            selectedItemProperties.trackId ||
            selectedItemProperties.TrackId
        )
      : null;
  const focusedTrackIds = this.getTrackFocusedTrackIds();
  const trackFeatures = Array.isArray(this.props.trackLayer.features)
    ? this.props.trackLayer.features
    : [];
  // When a user selects a track, the trackLayer payload is already scoped by
  // the entityId query, but trace features often lack agent metadata and a
  // stable track id. Do not filter those away.
  const agentFilteredTrackFeatures = selectedTrackId
    ? trackFeatures
    : trackFeatures.filter(feature =>
        this.featureMatchesSelectedAgents(feature, selectedAgentValues)
      );
  const filteredTrackFeatures = selectedTrackId
    ? agentFilteredTrackFeatures
    : agentFilteredTrackFeatures.filter(feature => {
        if (!this.featureMatchesSelectedAgents(feature, selectedAgentValues)) {
          return false;
        }
        const featureTrackId = this.getTrackFeatureId(feature);
        if (focusedTrackIds && focusedTrackIds.size > 0) {
          return !!featureTrackId && focusedTrackIds.has(featureTrackId);
        }
        return true;
      });
  const hasSingleVisibleTrack = filteredTrackFeatures.length === 1;
  // Unique track id count (not feature count - a track can in principle
  // appear as more than one feature) for MAX_CENTERPOINT_TRACKS enforcement
  // - see MapComponent.enforceCenterpointsTrackLimit.
  const visibleTrackIds = new Set();
  filteredTrackFeatures.forEach(feature => {
    const trackId = this.getTrackFeatureId(feature);
    if (trackId) visibleTrackIds.add(trackId);
  });
  this.latestActualTracksTrackCount = visibleTrackIds.size;
  const normalizeDateTimeValue = rawValue => {
    const value = String(rawValue || '').trim();
    if (!value) return '';
    return value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  };
  const parseDateTimeValue = rawValue => {
    const normalized = normalizeDateTimeValue(rawValue);
    if (!normalized) return null;
    const timestamp = Date.parse(normalized);
    return Number.isFinite(timestamp) ? timestamp : null;
  };
  const asCoordinate = value => {
    if (!Array.isArray(value) || value.length < 2) return null;
    const lng = Number(value[0]);
    const lat = Number(value[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return [lng, lat];
  };
  const parsePointLike = value => {
    if (!value) return null;
    if (Array.isArray(value)) return asCoordinate(value);
    if (typeof value === 'string') {
      try {
        return parsePointLike(JSON.parse(value));
      } catch (error) {
        return null;
      }
    }
    if (typeof value === 'object') {
      if (Array.isArray(value.coordinates)) {
        return asCoordinate(value.coordinates);
      }
    }
    return null;
  };
  const getAttributeValue = (feature, keys) => {
    if (!feature || !Array.isArray(feature.attributes)) return null;
    const normalizedKeys = Array.isArray(keys)
      ? keys.map(key => String(key || '').toLowerCase())
      : [];
    if (!normalizedKeys.length) return null;
    const attribute = feature.attributes.find(
      entry =>
        entry && normalizedKeys.includes(String(entry.key || '').toLowerCase())
    );
    return attribute ? attribute.value : null;
  };
  const getLinearCoordinates = geometry => {
    if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
      return [];
    }
    if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
      return geometry.coordinates;
    }
    if (geometry.type === 'MultiLineString') {
      return geometry.coordinates.reduce((acc, line) => {
        if (Array.isArray(line)) {
          line.forEach(coord => acc.push(coord));
        }
        return acc;
      }, []);
    }
    return [];
  };
  function resolveTrackEndpoints(feature) {
    const properties = (feature && feature.properties) || {};
    const startCoordCandidate =
      parsePointLike(feature && feature.startWhere) ||
      parsePointLike(properties.startWhere) ||
      parsePointLike(properties.StartWhere) ||
      parsePointLike(getAttributeValue(feature, ['startWhere', 'StartWhere']));
    const endCoordCandidate =
      parsePointLike(feature && feature.endWhere) ||
      parsePointLike(properties.endWhere) ||
      parsePointLike(properties.EndWhere) ||
      parsePointLike(getAttributeValue(feature, ['endWhere', 'EndWhere']));
    const startWhen =
      feature?.startWhen ||
      properties.startWhen ||
      properties.StartWhen ||
      getAttributeValue(feature, ['startWhen', 'StartWhen']);
    const endWhen =
      feature?.endWhen ||
      properties.endWhen ||
      properties.EndWhen ||
      getAttributeValue(feature, ['endWhen', 'EndWhen']);
    let startCoord = startCoordCandidate;
    let endCoord = endCoordCandidate;

    const coordinates = getLinearCoordinates(feature && feature.geometry);
    if (!startCoord || !endCoord) {
      const dateTimes = extractFeatureDateTimes(feature);
      const canInferFromTimeline =
        coordinates.length >= 2 && dateTimes.length === coordinates.length;
      if (canInferFromTimeline) {
        const coordinateTimeline = coordinates
          .map((coordinate, index) => {
            const validCoordinate = asCoordinate(coordinate);
            const timestamp = parseDateTimeValue(dateTimes[index]);
            if (!validCoordinate || timestamp === null) return null;
            return { coordinate: validCoordinate, timestamp, index };
          })
          .filter(Boolean);
        if (coordinateTimeline.length >= 2) {
          coordinateTimeline.sort((left, right) => {
            if (left.timestamp === right.timestamp)
              return left.index - right.index;
            return left.timestamp - right.timestamp;
          });
          if (!startCoord) {
            startCoord = coordinateTimeline[0].coordinate;
          }
          if (!endCoord) {
            endCoord =
              coordinateTimeline[coordinateTimeline.length - 1].coordinate;
          }
        }
      }
    }

    const startTimestamp = parseDateTimeValue(startWhen);
    const endTimestamp = parseDateTimeValue(endWhen);
    if (
      startCoord &&
      endCoord &&
      startTimestamp !== null &&
      endTimestamp !== null &&
      startTimestamp > endTimestamp
    ) {
      return { startCoord: endCoord, endCoord: startCoord };
    }

    if (!startCoord && coordinates.length > 0) {
      startCoord = asCoordinate(coordinates[0]);
    }
    if (!endCoord && coordinates.length > 0) {
      endCoord = asCoordinate(coordinates[coordinates.length - 1]);
    }
    return { startCoord, endCoord };
  }
  const extractFeatureDateTimes = feature => {
    if (!feature || typeof feature !== 'object') return [];
    const props = feature.properties || {};
    const candidates = [
      feature.dateTimes,
      feature.DateTimes,
      props.dateTimes,
      props.DateTimes,
      props.timestamps,
      props.Timestamps,
      props.timestamp,
      props.Timestamp
    ];
    for (let index = 0; index < candidates.length; index += 1) {
      const candidate = candidates[index];
      if (Array.isArray(candidate)) return candidate;
      if (typeof candidate === 'string' && candidate.trim()) return [candidate];
    }
    return [];
  };
  const getGeometryEndpoints = geometry => {
    if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
      return { first: null, last: null, pointCount: 0 };
    }
    if (geometry.type === 'LineString') {
      if (geometry.coordinates.length === 0) {
        return { first: null, last: null, pointCount: 0 };
      }
      return {
        first: geometry.coordinates[0],
        last: geometry.coordinates[geometry.coordinates.length - 1],
        pointCount: geometry.coordinates.length
      };
    }
    if (geometry.type === 'MultiLineString') {
      if (geometry.coordinates.length === 0) {
        return { first: null, last: null, pointCount: 0 };
      }
      const firstLine = geometry.coordinates.find(
        line => Array.isArray(line) && line.length > 0
      );
      const lastLine = [...geometry.coordinates]
        .reverse()
        .find(line => Array.isArray(line) && line.length > 0);
      if (!firstLine || !lastLine) {
        return { first: null, last: null, pointCount: 0 };
      }
      const pointCount = geometry.coordinates.reduce(
        (total, line) => total + (Array.isArray(line) ? line.length : 0),
        0
      );
      return {
        first: firstLine[0],
        last: lastLine[lastLine.length - 1],
        pointCount
      };
    }
    return { first: null, last: null, pointCount: 0 };
  };
  const reverseGeometryDirection = geometry => {
    if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
      return geometry;
    }
    if (geometry.type === 'LineString') {
      return {
        ...geometry,
        coordinates: geometry.coordinates.slice().reverse()
      };
    }
    if (geometry.type === 'MultiLineString') {
      return {
        ...geometry,
        coordinates: geometry.coordinates
          .slice()
          .reverse()
          .map(line => (Array.isArray(line) ? line.slice().reverse() : line))
      };
    }
    return geometry;
  };
  const orientGeometryForward = (feature, geometry) => {
    if (!feature || !geometry) return geometry;
    const endpoints = getGeometryEndpoints(geometry);
    if (!endpoints.first || !endpoints.last) return geometry;

    const { startCoord, endCoord } = resolveTrackEndpoints(feature);
    if (startCoord && endCoord) {
      const forwardCost =
        getCoordinateDistanceSq(endpoints.first, startCoord) +
        getCoordinateDistanceSq(endpoints.last, endCoord);
      const reverseCost =
        getCoordinateDistanceSq(endpoints.first, endCoord) +
        getCoordinateDistanceSq(endpoints.last, startCoord);
      if (
        (coordsAreNear(endpoints.first, endCoord) &&
          coordsAreNear(endpoints.last, startCoord)) ||
        reverseCost + 1e-10 < forwardCost
      ) {
        return reverseGeometryDirection(geometry);
      }
      return geometry;
    }

    const dateTimes = extractFeatureDateTimes(feature);
    if (dateTimes.length === endpoints.pointCount && dateTimes.length >= 2) {
      const firstTime = parseDateTimeValue(dateTimes[0]);
      const lastTime = parseDateTimeValue(dateTimes[dateTimes.length - 1]);
      if (firstTime !== null && lastTime !== null && firstTime > lastTime) {
        return reverseGeometryDirection(geometry);
      }
    }
    return geometry;
  };
  const normalizedTrackFeatures = filteredTrackFeatures.map(feature => {
    if (!feature) return feature;
    const colorKey = getTrackColorKeyFromFeature(feature);
    const preferredColor =
      this.props.trackColor === 'blue' ? '#0096FF' : '#FFAB00';
    const focusColor = hasSingleVisibleTrack
      ? preferredColor
      : getTrackColor(colorKey, preferredColor);

    let normalizedGeometry = feature.geometry;
    // Ensure all track geometries are consistently oriented in forward time.
    if (normalizedGeometry && normalizedGeometry.type === 'MultiPoint') {
      const rawCoordinates = Array.isArray(normalizedGeometry.coordinates)
        ? normalizedGeometry.coordinates
        : [];
      if (rawCoordinates.length >= 2) {
        normalizedGeometry = {
          type: 'LineString',
          coordinates: rawCoordinates.slice()
        };
      }
    } else if (normalizedGeometry && normalizedGeometry.type === 'LineString') {
      normalizedGeometry = {
        ...normalizedGeometry,
        coordinates: Array.isArray(normalizedGeometry.coordinates)
          ? normalizedGeometry.coordinates.slice()
          : []
      };
    } else if (
      normalizedGeometry &&
      normalizedGeometry.type === 'MultiLineString'
    ) {
      normalizedGeometry = {
        ...normalizedGeometry,
        coordinates: Array.isArray(normalizedGeometry.coordinates)
          ? normalizedGeometry.coordinates.map(line =>
              Array.isArray(line) ? line.slice() : line
            )
          : []
      };
    }
    normalizedGeometry = orientGeometryForward(feature, normalizedGeometry);

    // Promote top-level fields that Mapbox GL won't expose in e.features[0].properties
    const props = feature.properties || {};
    const agent = feature.agent || props.agent || {};
    // Check attributes array for 'username' key (same as getAgentDisplayName)
    const usernameFromAttributes = (() => {
      const attrs = Array.isArray(feature.attributes) ? feature.attributes : [];
      const entry = attrs.find(
        a =>
          a &&
          String(a.key || '')
            .trim()
            .toLowerCase() === 'username'
      );
      return entry ? String(entry.value || '').trim() : '';
    })();
    // Filter out UUID/hex-like identifiers (they are not human-readable names)
    const pickName = (...candidates) => {
      for (const c of candidates) {
        const s = c ? String(c).trim() : '';
        if (s && !isCodeLikeName(s)) return s;
      }
      return '';
    };
    const hoverAgentName = pickName(
      agent.agentName,
      feature.agentName,
      feature.AgentName,
      props.agentName,
      props.AgentName,
      usernameFromAttributes,
      props.Name,
      props.EntityName
    );
    const hoverFeatureId =
      feature.featureId ||
      props.featureId ||
      props.EntityId ||
      props.trackId ||
      '';
    const hoverStartWhen =
      feature.startWhen || props.startWhen || props.StartWhen || '';
    const hoverDateTimes =
      feature.dateTimes ||
      feature.DateTimes ||
      props.dateTimes ||
      props.DateTimes ||
      null;

    return {
      ...feature,
      ...(normalizedGeometry ? { geometry: normalizedGeometry } : {}),
      properties: {
        ...(feature.properties || {}),
        ...(focusColor ? { focusColor } : {}),
        ...(hoverAgentName ? { _hoverAgentName: hoverAgentName } : {}),
        ...(hoverFeatureId ? { _hoverFeatureId: hoverFeatureId } : {}),
        ...(hoverStartWhen ? { _hoverStartWhen: hoverStartWhen } : {}),
        ...(hoverDateTimes
          ? { _hoverDateTimes: JSON.stringify(hoverDateTimes) }
          : {})
      }
    };
  });

  // 'colourCoded' replaces the single flat-coloured line per track with
  // many short per-hour segments (see tracksFullColour.toColouredSegments,
  // reused here rather than duplicated - same hour-of-day gradient the
  // former "Full tracks - colour coded" bulk menu option used). Restricted
  // to a single visible track: colour-coding every segment of many
  // overlapping tracks at once produced a GeoJSON payload large enough to
  // throw "Invalid string length" in the mapbox-gl worker, on top of
  // already being unreadable at scale (see MAX_COLOUR_CODED_TRACKS in
  // tracksFullColour.js). Falls through to the plain per-track line for
  // 'yellow'/'blue', or whenever more than one track is visible.
  const isColourCoded =
    this.props.trackColor === 'colourCoded' && hasSingleVisibleTrack;
  // Lets MapComponent's render() show the same hour-colour legend for this
  // single track that the old bulk "Full tracks - colour coded" mode used
  // (see MapComponent.renderTrackHourColourLegend/setSingleTrackColourCoded)
  // without duplicating the trackColor+hasSingleVisibleTrack check there.
  if (this.setSingleTrackColourCoded) {
    this.setSingleTrackColourCoded(isColourCoded);
  }
  const lineFeaturesForRender = isColourCoded
    ? normalizedTrackFeatures.reduce((all, feature) => {
        if (!feature || !feature.geometry) return all;
        return all.concat(toColouredSegments(feature));
      }, [])
    : normalizedTrackFeatures;

  const tracePointFeatures = [];
  normalizedTrackFeatures.forEach(feature => {
    if (!feature || !feature.geometry) return;
    const properties = feature.properties || {};
    const renderProperties = {};
    if (properties.focusColor)
      renderProperties.focusColor = properties.focusColor;
    if (properties.color) renderProperties.color = properties.color;
    const addTracePoint = (coord, hourColor) => {
      if (!Array.isArray(coord) || coord.length < 2) return;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      tracePointFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        properties: {
          ...renderProperties,
          ...(hourColor ? { hourColor } : {}),
          _tracePoint: true
        }
      });
    };

    // Colour-coded dots follow the same per-point hour-of-day timing as the
    // line segments above, so both stay consistent. Falls back to the plain
    // (untagged) point loop below when there's no usable per-point timing -
    // same "don't silently drop the track" reasoning as toColouredSegments.
    if (isColourCoded) {
      const timed = extractTimedCoordinates(feature);
      if (timed.length > 0) {
        timed.forEach(entry =>
          addTracePoint(entry.coordinate, getHourColor(entry.hour))
        );
        return;
      }
    }

    if (
      feature.geometry.type === 'LineString' &&
      Array.isArray(feature.geometry.coordinates)
    ) {
      feature.geometry.coordinates.forEach(coord => addTracePoint(coord));
      return;
    }
    if (
      feature.geometry.type === 'MultiLineString' &&
      Array.isArray(feature.geometry.coordinates)
    ) {
      feature.geometry.coordinates.forEach(line => {
        if (Array.isArray(line)) line.forEach(coord => addTracePoint(coord));
      });
    }
  });

  let data = {
    type: this.props.trackLayer.type,
    features: lineFeaturesForRender.concat(tracePointFeatures)
  };

  const endpointCandidateFeatures = normalizedTrackFeatures.filter(feature => {
    const props = (feature && feature.properties) || {};
    return !props._tracePoint;
  });

  const startFeatures = [];
  const getBearingDegrees = (from, to) => {
    if (
      !Array.isArray(from) ||
      !Array.isArray(to) ||
      from.length < 2 ||
      to.length < 2
    ) {
      return 0;
    }
    const lon1 = (from[0] * Math.PI) / 180;
    const lat1 = (from[1] * Math.PI) / 180;
    const lon2 = (to[0] * Math.PI) / 180;
    const lat2 = (to[1] * Math.PI) / 180;
    const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
    const bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360;
  };
  const collectGeometryCoordinates = geometry => {
    if (!geometry || !geometry.type || !geometry.coordinates) return [];
    if (geometry.type === 'Point') {
      return Array.isArray(geometry.coordinates) ? [geometry.coordinates] : [];
    }
    if (geometry.type === 'LineString') {
      return Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    }
    if (geometry.type === 'MultiPoint') {
      return Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    }
    if (geometry.type === 'MultiLineString') {
      if (!Array.isArray(geometry.coordinates)) return [];
      return geometry.coordinates.reduce((acc, line) => {
        if (Array.isArray(line)) {
          line.forEach(coord => acc.push(coord));
        }
        return acc;
      }, []);
    }
    return [];
  };
  const getTimelineTrackGeoFeature = trackId => {
    const normalizedTrackId = this.normalizeTrackIdentifier(trackId);
    const timelineResults = Array.isArray(this.props.timelineResults)
      ? this.props.timelineResults
      : [];
    for (let index = 0; index < timelineResults.length; index += 1) {
      const result = timelineResults[index];
      const content = Array.isArray(result && result.extracted?.content)
        ? result.extracted.content
        : [];
      const geoFeatureEntry = content.find(
        entry => entry && entry.GeoFeature && this.isObject(entry.GeoFeature)
      );
      if (!geoFeatureEntry || !geoFeatureEntry.GeoFeature) continue;
      const candidate = geoFeatureEntry.GeoFeature;
      const candidateId = this.normalizeTrackIdentifier(
        candidate.featureId ||
          candidate.EntityId ||
          candidate.trackId ||
          candidate.TrackId
      );
      if (candidateId && candidateId === normalizedTrackId) {
        return candidate;
      }
    }
    return null;
  };
  const seenTrackStartKeys = new Set();
  endpointCandidateFeatures.forEach(feature => {
    if (!feature || !feature.geometry) return;
    const properties = feature.properties || {};
    const endpointRenderProps = { ...properties };
    delete endpointRenderProps._hoverDateTimes;
    const trackKey =
      properties.EntityId ||
      properties.FeatureId ||
      properties.featureId ||
      properties.trackId ||
      feature.featureId ||
      JSON.stringify(feature.geometry).slice(0, 80);
    if (seenTrackStartKeys.has(trackKey)) return;

    let startCoord = null;
    let endCoord = null;
    const explicitEndpoints = resolveTrackEndpoints(feature);
    if (explicitEndpoints.startCoord) {
      startCoord = explicitEndpoints.startCoord;
      endCoord = explicitEndpoints.endCoord || explicitEndpoints.startCoord;
    }
    if (
      !startCoord &&
      feature.geometry.type === 'Point' &&
      Array.isArray(feature.geometry.coordinates)
    ) {
      startCoord = feature.geometry.coordinates;
      endCoord = feature.geometry.coordinates;
    } else if (
      !startCoord &&
      feature.geometry.type === 'LineString' &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length > 0
    ) {
      startCoord = feature.geometry.coordinates[0];
      endCoord =
        feature.geometry.coordinates[feature.geometry.coordinates.length - 1];
    } else if (
      !startCoord &&
      feature.geometry.type === 'MultiPoint' &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length > 0
    ) {
      startCoord = feature.geometry.coordinates[0];
      endCoord =
        feature.geometry.coordinates[feature.geometry.coordinates.length - 1];
    } else if (
      !startCoord &&
      feature.geometry.type === 'MultiLineString' &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length > 0 &&
      Array.isArray(feature.geometry.coordinates[0]) &&
      feature.geometry.coordinates[0].length > 0
    ) {
      startCoord = feature.geometry.coordinates[0][0];
      const lastLine =
        feature.geometry.coordinates[feature.geometry.coordinates.length - 1];
      if (Array.isArray(lastLine) && lastLine.length > 0) {
        endCoord = lastLine[lastLine.length - 1];
      }
    }
    if (!Array.isArray(startCoord) || startCoord.length < 2) return;
    const flattenedCoordinates = collectGeometryCoordinates(feature.geometry);
    const startDirectionAngle =
      flattenedCoordinates.length > 1
        ? getBearingDegrees(flattenedCoordinates[0], flattenedCoordinates[1])
        : Array.isArray(endCoord) && endCoord.length >= 2
        ? getBearingDegrees(startCoord, endCoord)
        : 0;
    const endDirectionAngle =
      flattenedCoordinates.length > 1
        ? getBearingDegrees(
            flattenedCoordinates[flattenedCoordinates.length - 2],
            flattenedCoordinates[flattenedCoordinates.length - 1]
          )
        : Array.isArray(endCoord) && endCoord.length >= 2
        ? getBearingDegrees(startCoord, endCoord)
        : startDirectionAngle;

    seenTrackStartKeys.add(trackKey);
    startFeatures.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: startCoord
      },
      properties: {
        ...endpointRenderProps,
        markerKind: 'start',
        directionAngle: startDirectionAngle
      }
    });
    if (
      Array.isArray(endCoord) &&
      endCoord.length >= 2 &&
      (endCoord[0] !== startCoord[0] || endCoord[1] !== startCoord[1])
    ) {
      startFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: endCoord
        },
        properties: {
          ...endpointRenderProps,
          markerKind: 'end',
          directionAngle: endDirectionAngle
        }
      });
    }
  });

  let startFeature = {
    type: 'FeatureCollection',
    features: startFeatures
  };

  if (selectedTrackId) {
    const selectedContextFeature =
      (this.props.selectedItem &&
        (this.trackIdsMatch(
          this.getTrackFeatureId(this.props.selectedItem),
          selectedTrackId
        ) ||
          this.trackIdsMatch(
            this.props.selectedItem.featureId,
            selectedTrackId
          )) &&
        this.props.selectedItem) ||
      getTimelineTrackGeoFeature(selectedTrackId);
    const selectedContextEndpoints = resolveTrackEndpoints(
      selectedContextFeature
    );
    if (selectedContextEndpoints.startCoord) {
      const firstCoord = selectedContextEndpoints.startCoord;
      const lastCoord =
        selectedContextEndpoints.endCoord ||
        selectedContextEndpoints.startCoord;
      const endpointFeatures = [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: firstCoord
          },
          properties: {
            markerKind: 'start',
            directionAngle: getBearingDegrees(firstCoord, lastCoord)
          }
        }
      ];
      if (
        lastCoord &&
        (lastCoord[0] !== firstCoord[0] || lastCoord[1] !== firstCoord[1])
      ) {
        endpointFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: lastCoord
          },
          properties: {
            markerKind: 'end',
            directionAngle: getBearingDegrees(firstCoord, lastCoord)
          }
        });
      }
      startFeature = {
        type: 'FeatureCollection',
        features: endpointFeatures
      };
    }

    const selectedTrackFeatureWithEndpoints = endpointCandidateFeatures.find(
      feature => {
        const featureTrackId = this.getTrackFeatureId(feature);
        if (!this.trackIdsMatch(featureTrackId, selectedTrackId)) {
          return false;
        }
        const endpoints = resolveTrackEndpoints(feature);
        return !!endpoints.startCoord;
      }
    );
    if (!startFeature.features.length && selectedTrackFeatureWithEndpoints) {
      const selectedTrackEndpoints = resolveTrackEndpoints(
        selectedTrackFeatureWithEndpoints
      );
      const firstCoord = selectedTrackEndpoints.startCoord;
      const lastCoord =
        selectedTrackEndpoints.endCoord || selectedTrackEndpoints.startCoord;
      const endpointFeatures = [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: firstCoord
          },
          properties: {
            markerKind: 'start',
            directionAngle: getBearingDegrees(firstCoord, lastCoord)
          }
        }
      ];
      if (
        lastCoord &&
        (lastCoord[0] !== firstCoord[0] || lastCoord[1] !== firstCoord[1])
      ) {
        endpointFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: lastCoord
          },
          properties: {
            markerKind: 'end',
            directionAngle: getBearingDegrees(firstCoord, lastCoord)
          }
        });
      }
      startFeature = {
        type: 'FeatureCollection',
        features: endpointFeatures
      };
    }
    const selectedTrackCoordinates = endpointCandidateFeatures.reduce(
      (acc, feature) => {
        const coords = collectGeometryCoordinates(feature && feature.geometry);
        coords.forEach(coord => {
          if (Array.isArray(coord) && coord.length >= 2) {
            acc.push(coord);
          }
        });
        return acc;
      },
      []
    );
    if (!startFeature.features.length && selectedTrackCoordinates.length > 0) {
      const firstCoord = selectedTrackCoordinates[0];
      const lastCoord =
        selectedTrackCoordinates[selectedTrackCoordinates.length - 1];
      const endpointFeatures = [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: firstCoord
          },
          properties: {
            markerKind: 'start',
            directionAngle:
              selectedTrackCoordinates.length > 1
                ? getBearingDegrees(
                    selectedTrackCoordinates[0],
                    selectedTrackCoordinates[1]
                  )
                : 0
          }
        }
      ];
      if (
        lastCoord &&
        (lastCoord[0] !== firstCoord[0] || lastCoord[1] !== firstCoord[1])
      ) {
        const selectedTrackSecondLastCoordinate =
          selectedTrackCoordinates[selectedTrackCoordinates.length - 2];
        const selectedTrackLastCoordinate =
          selectedTrackCoordinates[selectedTrackCoordinates.length - 1];
        endpointFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: lastCoord
          },
          properties: {
            markerKind: 'end',
            directionAngle:
              selectedTrackCoordinates.length > 1
                ? getBearingDegrees(
                    selectedTrackSecondLastCoordinate,
                    selectedTrackLastCoordinate
                  )
                : 0
          }
        });
      }
      startFeature = {
        type: 'FeatureCollection',
        features: endpointFeatures
      };
    }
  }

  this.latestActualTracksData = data;
  // Render endpoints first (cheap + already-known), then render the heavier
  // track geometry on the next frame to keep the UI feeling instant.
  const endpointsSource =
    this.map && this.map.getSource(this.trackEndpointsSourceId);
  if (endpointsSource) endpointsSource.setData(startFeature);

  const tracksSource = this.map && this.map.getSource('actualTracks');
  if (tracksSource) {
    const canUseRaf =
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function';
    if (this.pendingActualTracksFrame !== null) {
      if (
        this.pendingActualTracksFrameUsesRaf &&
        typeof window !== 'undefined' &&
        typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(this.pendingActualTracksFrame);
      } else {
        clearTimeout(this.pendingActualTracksFrame);
      }
      this.pendingActualTracksFrame = null;
      this.pendingActualTracksFrameUsesRaf = false;
    }
    const schedule = canUseRaf
      ? window.requestAnimationFrame
      : fn => setTimeout(fn, 0);
    this.pendingActualTracksFrameUsesRaf = canUseRaf;
    this.pendingActualTracksFrame = schedule(() => {
      this.pendingActualTracksFrame = null;
      this.pendingActualTracksFrameUsesRaf = false;
      // Map might have been unmounted between scheduling and execution.
      const latestTracksSource = this.map && this.map.getSource('actualTracks');
      if (latestTracksSource) latestTracksSource.setData(data);
    });
  }
}
